// 활동 이력 수동 항목 편집 폼 — 추가/수정 공용(한 폼을 모드만 바꿔 재사용).
//
// 스타일은 Profile 블록 관례 그대로: radius 0, LCMD 시맨틱 토큰, 포커스는 블루.
// 저장 실패는 삼키지 않는다 — 서버가 준 사유(연도 범위·길이 등)를 그대로 캡션으로 보여준다.
import { useState } from 'react'
import type { NotableHistoryInput } from '../../notableHistoryApi'

interface Props {
  initial?: { year: number; note: string }
  submitLabel: string
  onSubmit: (input: NotableHistoryInput) => Promise<void>
  onCancel: () => void
}

const INPUT_CLS =
  'border border-line-soft bg-surface-base px-2 py-1.5 font-text text-[13px] text-fg-1 placeholder:text-fg-3 focus:border-brand-blue focus:outline-none'

export function NotableHistoryEditor({ initial, submitLabel, onSubmit, onCancel }: Props) {
  const [year, setYear] = useState(initial ? String(initial.year) : '')
  const [note, setNote] = useState(initial?.note ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit() {
    if (saving) return
    setError(null)
    const y = Number(year.trim())
    // 서버가 최종 판정하지만, 왕복 없이 잡을 수 있는 형식 오류는 여기서 즉시 알린다.
    if (!Number.isInteger(y)) {
      setError('연도를 숫자 4자리로 입력해주세요.')
      return
    }
    if (!note.trim()) {
      setError('내용을 입력해주세요.')
      return
    }
    setSaving(true)
    try {
      await onSubmit({ year: y, note: note.trim() })
    } catch (e) {
      setError((e as { message?: string })?.message ?? '저장하지 못했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-2 border border-line-soft bg-surface-soft p-3">
      <div className="flex flex-wrap items-start gap-2">
        <input
          value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder="2024"
          inputMode="numeric"
          aria-label="연도"
          className={`${INPUT_CLS} w-[72px] tabular-nums`}
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit()
            if (e.key === 'Escape') onCancel()
          }}
          placeholder="예: 대한민국 광고대상 수상"
          aria-label="내용"
          className={`${INPUT_CLS} min-w-0 flex-1`}
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving}
          className="bg-brand-blue px-3 py-1.5 font-text text-[12px] font-medium text-white disabled:opacity-50"
        >
          {saving ? '저장 중…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="border border-line-soft px-3 py-1.5 font-text text-[12px] text-fg-2 hover:bg-surface-base"
        >
          취소
        </button>
        {error && <span className="break-keep font-text text-[11px] text-status-danger">{error}</span>}
      </div>
    </div>
  )
}
